import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ProductShowcase() {
  const navigate = useNavigate();
  const [activeShowcase, setActiveShowcase] = useState('admin');

  const items = [
    {
      id: 'admin',
      label: 'Admin Control Center',
      title: 'Global School Management & Analytics',
      desc: 'Monitor enrollment KPIs, manage teacher/student user profiles, link parent accounts, and review global attendance percentages.',
      mockupTitle: 'EduPortal Admin Console — Overview & Analytics',
      metrics: [
        { name: 'Active Students', val: '1,280' },
        { name: 'Teachers', val: '42' },
        { name: 'Pass Rate', val: '98.2%' },
      ],
      featuresList: [
        'User account creation & password resets',
        'Academic class & subject configuration',
        'Parent-Student account linkage manager',
        'Global Grade Book & PDF export',
      ],
    },
    {
      id: 'teacher',
      label: 'Teacher Classroom Suite',
      title: 'Attendance, Quiz Builder & Grade Book',
      desc: 'Mark daily roll call with one click, build timed multiple-choice quizzes, grade assignment submissions, and send class notices.',
      mockupTitle: 'EduPortal Teacher Workspace — Grade 10 Physics',
      metrics: [
        { name: 'Class Roll Call', val: '100% Logged' },
        { name: 'Active Quizzes', val: '14 Created' },
        { name: 'Avg Score', val: '91.4%' },
      ],
      featuresList: [
        'Daily attendance tracking sheet',
        'Automated MCQ quiz builder with timers',
        'Digital homework collection & feedback',
        'Weekly class timetable schedule',
      ],
    },
    {
      id: 'student',
      label: 'Student Workspace',
      title: 'Quizzes, Homework & Grade Tracking',
      desc: 'Take online quizzes with countdown timers, submit homework documents, view weekly class schedules, and track GPA standing.',
      mockupTitle: 'EduPortal Student Dashboard — Learning Hub',
      metrics: [
        { name: 'Current GPA', val: '3.92' },
        { name: 'Quizzes Done', val: '22' },
        { name: 'Attendance', val: '98%' },
      ],
      featuresList: [
        'Interactive timed online quiz solver',
        'Assignment submission status tracker',
        'Subject-by-subject grade breakdown',
        'Unread notifications alert bell',
      ],
    },
    {
      id: 'parent',
      label: 'Parent Portal',
      title: 'Attendance Calendar & Report Cards',
      desc: 'Stay informed with live child attendance status, auto-graded quiz marks, teacher notes, and instant downloadable PDF report cards.',
      mockupTitle: 'EduPortal Parent Console — Student Progress',
      metrics: [
        { name: 'Attendance Rate', val: '96.4%' },
        { name: 'Report Cards', val: 'PDF Available' },
        { name: 'Teacher Messages', val: '3 New' },
      ],
      featuresList: [
        'Visual monthly attendance calendar',
        'Quiz score history & grade breakdown',
        'Direct teacher messaging channel',
        '1-Click PDF Report Card export',
      ],
    },
  ];

  const current = items.find((item) => item.id === activeShowcase) || items[0];

  return (
    <section className="py-24 bg-slate-950 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">
            PRODUCT SHOWCASE
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">
            See EduPortal in Action
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            Explore authentic user interfaces designed for speed, clarity, and daily productivity.
          </p>
        </div>

        {/* Gallery Navigation Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveShowcase(item.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                activeShowcase === item.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : 'bg-white/5 text-slate-400 hover:text-white border border-white/10'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Display Frame */}
        <div className="bg-slate-900/90 border border-white/15 rounded-3xl p-6 md:p-8 shadow-2xl backdrop-blur-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Showcase Info */}
            <div className="lg:col-span-5 space-y-6 text-left">
              <h3 className="text-2xl font-bold text-white leading-tight">
                {current.title}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {current.desc}
              </p>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3 bg-white/5 border border-white/10 p-3 rounded-2xl">
                {current.metrics.map((m, i) => (
                  <div key={i} className="text-center">
                    <div className="text-[10px] text-slate-400 uppercase font-semibold">{m.name}</div>
                    <div className="text-base font-extrabold text-white mt-0.5">{m.val}</div>
                  </div>
                ))}
              </div>

              {/* Features checklist */}
              <ul className="space-y-2 text-xs text-slate-300 font-medium">
                {current.featuresList.map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate('/select-role')}
                className="w-full sm:w-auto text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 rounded-xl shadow-lg hover:brightness-110 transition-all"
              >
                Try {current.label} Now →
              </button>
            </div>

            {/* Showcase Visual Mockup */}
            <div className="lg:col-span-7">
              <div className="bg-slate-950 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                {/* Window Bar */}
                <div className="bg-slate-900 border-b border-white/10 px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                  </div>
                  <div className="text-xs text-slate-400 font-mono truncate max-w-xs">
                    {current.mockupTitle}
                  </div>
                  <div className="text-xs text-emerald-400 font-bold">● Active</div>
                </div>

                {/* Simulated UI Content */}
                <div className="p-6 space-y-4 text-left">
                  <div className="flex items-center justify-between pb-3 border-b border-white/10">
                    <div>
                      <div className="text-xs text-blue-400 font-bold">EDUPORTAL APP ENGINE</div>
                      <div className="text-lg font-extrabold text-white">{current.label} Dashboard</div>
                    </div>
                    <span className="bg-blue-600/20 text-blue-300 text-xs font-bold px-3 py-1 rounded-full border border-blue-500/30">
                      Real Application View
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 border border-white/5 p-3 rounded-xl">
                      <div className="text-[11px] text-slate-400">System Status</div>
                      <div className="text-sm font-bold text-emerald-400 mt-1">Operational (100% Uptime)</div>
                    </div>
                    <div className="bg-white/5 border border-white/5 p-3 rounded-xl">
                      <div className="text-[11px] text-slate-400">Data Synchronization</div>
                      <div className="text-sm font-bold text-blue-400 mt-1">Auto JWT Refreshed</div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/5 p-4 rounded-xl space-y-2">
                    <div className="flex justify-between text-xs text-slate-300 font-semibold">
                      <span>Module Health & Response Speed</span>
                      <span className="text-emerald-400">&lt; 45ms</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div className="bg-emerald-500 h-full w-[96%]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
